/**
 * Manejo uniforme de errores al consumir la API desde el playground.
 *
 * - `apiFetch`: igual que `fetch`, pero un fallo de red (TypeError "Failed to fetch") se convierte
 *   en un mensaje entendible. El caso típico es un timeout: Azure App Service corta la petición a
 *   los ~230 s y su respuesta de error no trae headers CORS, así que el navegador no deja leerla y
 *   `fetch` solo reporta "Failed to fetch".
 * - `readApiError`: arma el mensaje de una respuesta no-2xx. La API devuelve `message`
 *   (`[RequireFeature]`, `RateLimitFilter`, Términos), `errorMessage` (respuestas de consulta) o
 *   `ProblemDetails` (`title`/`detail`), y para el 403 de cuota agrega el uso y el límite.
 */

export const NETWORK_ERROR_MESSAGE =
  'No se recibió respuesta del servidor. La consulta pudo exceder el tiempo de espera ' +
  '(el portal de la autoridad puede estar lento) o se perdió la conexión. Intenta de nuevo en unos minutos.';

export async function apiFetch(url, options) {
  try {
    return await fetch(url, options);
  } catch (err) {
    if (err?.name === 'AbortError') throw err;
    throw new Error(NETWORK_ERROR_MESSAGE);
  }
}

const isNum = (v) => typeof v === 'number' && Number.isFinite(v);

/** Detalle de cuota del 403 de [RequireFeature] (uso/límite diario y mensual). */
function describeQuota(body) {
  const parts = [];
  if (isNum(body.dailyLimit) && body.dailyLimit > 0)
    parts.push(`Uso diario: ${body.dailyUsage ?? 0} de ${body.dailyLimit}.`);
  if (isNum(body.monthlyLimit) && body.monthlyLimit > 0)
    parts.push(`Uso mensual: ${body.monthlyUsage ?? 0} de ${body.monthlyLimit}.`);
  return parts.join(' ');
}

const withPeriod = (s) => (/[.!?]$/.test(s) ? s : `${s}.`);

function genericMessage(status) {
  switch (status) {
    case 401: return 'Tu sesión expiró. Vuelve a iniciar sesión.';
    case 403: return 'No tienes acceso a esta operación con tu plan actual.';
    case 429: return 'Demasiadas peticiones. Espera un momento e intenta de nuevo.';
    case 502:
    case 503:
    case 504: return 'El servicio no respondió a tiempo. Intenta de nuevo en unos minutos.';
    default:  return `Error ${status}`;
  }
}

/** Mensaje legible a partir de una respuesta no-2xx (consume el cuerpo). */
export async function readApiError(res) {
  let text = '';
  try { text = await res.text(); } catch { /* cuerpo ilegible */ }

  let body = null;
  if (text) {
    try { body = JSON.parse(text); } catch { /* no es JSON */ }
  }

  if (body && typeof body === 'object') {
    let msg = body.errorMessage || body.message || body.detail || body.title || '';
    if (res.status === 403) {
      const quota = describeQuota(body);
      if (quota) msg = `${withPeriod(msg || genericMessage(403))} ${quota}`;
      if (body.featureCode && (body.dailyLimit != null || body.monthlyLimit != null))
        msg += ' Puedes ampliar tus límites cambiando de plan en Suscripciones.';
    }
    if (res.status === 429 && isNum(body.retryAfterSeconds))
      msg = `${withPeriod(msg || genericMessage(429))} Reintenta en ${body.retryAfterSeconds} s.`;
    return msg || genericMessage(res.status);
  }

  // Texto plano corto (BadRequest("...")); el HTML de un gateway no se muestra tal cual.
  const trimmed = text.trim();
  if (trimmed && !trimmed.startsWith('<') && trimmed.length <= 500) return trimmed;
  return genericMessage(res.status);
}

/** Lanza un Error con el mensaje de la API si la respuesta no es 2xx. */
export async function ensureOk(res) {
  if (!res.ok) throw new Error(await readApiError(res));
  return res;
}
